import * as THREE from 'three';
import type { Controls } from '../core/device';
import type { Player } from '../entities/Player';
import type { MiniGame, MiniInput } from '../minigames/types';
import type { Pose } from '../world/builders/character';
import type { Vec2 } from '../world/layout';
import type { Stage, StageButton, StageHud, StageInput } from './types';

/** Where the camera is and what it's looking at (world space). */
export interface Shot {
  from: THREE.Vector3;
  at: THREE.Vector3;
  fov: number;
}

/**
 * How to play a mini-game's logic out in the world: where you stand, what pose you
 * strike, where the camera goes, what the HUD shows and what moves in 3D.
 */
export interface WorldGameSpec<G extends MiniGame> {
  game: G;
  stand: Vec2;
  facing: number;
  pose(t: number): Pose;
  shot(t: number, out: Shot): void;
  hud(): StageHud;
  hint?(controls: Controls): string;
  buttons?(touch: boolean): StageButton[];
  /** Turn stage input into what the game expects (default: the same buttons). */
  input?(i: StageInput): MiniInput;
  /** 3D props and effects, every frame (after the game updates). */
  tick?(dt: number, t: number): void;
  begin?(): void;
  end?(): void;
  /** Extra time after the game says it's done (an animation finishing). */
  linger?(): boolean;
}

/** Runs a WorldGameSpec as a stage. */
export class WorldGameStage<G extends MiniGame> implements Stage {
  readonly crosshair = false;
  readonly pointerLock = false;
  private t = 0;
  private baseFov = 60;
  private camera: THREE.PerspectiveCamera | null = null;
  private readonly shotBuf: Shot = { from: new THREE.Vector3(), at: new THREE.Vector3(), fov: 55 };

  constructor(
    readonly spec: WorldGameSpec<G>,
    private readonly player: Player,
  ) {}

  get game(): G {
    return this.spec.game;
  }

  get title(): string {
    return this.spec.game.title;
  }

  get done(): boolean {
    return this.spec.game.done && !(this.spec.linger?.() ?? false);
  }

  hint(controls: Controls): string {
    return this.spec.hint?.(controls) ?? this.spec.game.hint(controls);
  }

  buttons(touch: boolean): StageButton[] {
    return this.spec.buttons?.(touch) ?? [];
  }

  begin(): void {
    const { spec, player } = this;
    player.teleport(spec.stand.x, spec.stand.z, spec.facing);
    player.scripted = true;
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: spec.pose(0) });
    player.syncTransform();
    spec.begin?.();
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    const { spec, player } = this;
    const mini: MiniInput = spec.input?.(input) ?? {
      held: input.held,
      pressed: input.pressed || (input.fire && !input.pressed),
      released: input.released,
      tap: null,
      keys: input.keys,
    };
    spec.game.update(dt, mini);
    player.anim.update({ dt, time: this.t, speed: 0, pose: spec.pose(this.t) });
    spec.tick?.(dt, this.t);
  }

  frame(camera: THREE.PerspectiveCamera): void {
    if (this.camera !== camera) {
      this.camera = camera;
      this.baseFov = camera.fov;
    }
    const s = this.shotBuf;
    this.spec.shot(this.t, s);
    camera.position.copy(s.from);
    camera.lookAt(s.at);
    if (Math.abs(camera.fov - s.fov) > 0.01) {
      camera.fov = s.fov;
      camera.updateProjectionMatrix();
    }
  }

  hud(): StageHud {
    return this.spec.hud();
  }

  end(): void {
    if (this.camera) {
      this.camera.fov = this.baseFov;
      this.camera.updateProjectionMatrix();
    }
    this.spec.end?.();
    this.player.scripted = false;
    this.player.syncTransform();
  }
}
